import type { UniverseFrame } from '@/lib/universe/store'

import { computeCamera, createBasis } from '@/lib/universe/camera'
import { VERTEX_SHADER, buildFragmentShader } from '@/lib/universe/shader'

export interface BlackHoleRenderer {
    render: (frame: UniverseFrame) => void
    dispose: () => void
}

const UNIFORMS = [
    'u_resolution',
    'u_time',
    'u_camPos',
    'u_camRight',
    'u_camUp',
    'u_camForward',
    'u_tanHalfFov',
    'u_shift',
    'u_disk',
    'u_ring',
    'u_stars',
    'u_jet',
    'u_exposure',
    'u_reveal',
    'u_pointer',
    'u_pointerPull',
    'u_stepScale',
    'u_streak'
] as const

type UniformName = typeof UNIFORMS[number]

const compileShader = (gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null => {
    const shader = gl.createShader(type)

    if (!shader) return null

    gl.shaderSource(shader, source)
    gl.compileShader(shader)

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error('Black hole shader failed to compile:', gl.getShaderInfoLog(shader))
        gl.deleteShader(shader)

        return null
    }

    return shader
}

const createProgram = (gl: WebGLRenderingContext, fragmentSource: string): WebGLProgram | null => {
    const vertex = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER)
    const fragment = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource)

    if (!vertex || !fragment) return null

    const program = gl.createProgram()

    if (!program) return null

    gl.attachShader(program, vertex)
    gl.attachShader(program, fragment)
    gl.linkProgram(program)
    gl.deleteShader(vertex)
    gl.deleteShader(fragment)

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.error('Black hole program failed to link:', gl.getProgramInfoLog(program))
        gl.deleteProgram(program)

        return null
    }

    return program
}

export const createRenderer = (canvas: HTMLCanvasElement, onFailure: () => void): BlackHoleRenderer | null => {
    let gl: WebGLRenderingContext | null = null

    try {
        gl = canvas.getContext('webgl', {
            alpha: false,
            antialias: false,
            depth: false,
            stencil: false,
            premultipliedAlpha: false,
            preserveDrawingBuffer: false,
            powerPreference: 'high-performance'
        }) as WebGLRenderingContext | null
    }
    catch {
        gl = null
    }

    if (!gl) return null

    const context = gl
    const derivatives = context.getExtension('OES_standard_derivatives') !== null
    const program = createProgram(context, buildFragmentShader(derivatives))

    if (!program) return null

    const buffer = context.createBuffer()

    context.bindBuffer(context.ARRAY_BUFFER, buffer)
    context.bufferData(context.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), context.STATIC_DRAW)
    context.useProgram(program)

    const position = context.getAttribLocation(program, 'a_position')

    context.enableVertexAttribArray(position)
    context.vertexAttribPointer(position, 2, context.FLOAT, false, 0, 0)

    const uniforms = {} as Record<UniformName, WebGLUniformLocation | null>

    UNIFORMS.forEach(name => {
        uniforms[name] = context.getUniformLocation(program, name)
    })

    const compact = window.matchMedia('(max-width: 767px), (pointer: coarse)').matches
    const minQuality = 0.3
    const maxQuality = compact ? 0.72 : 1
    const maxPixels = compact ? 600000 : 1700000

    let quality = compact ? 0.55 : 0.8
    let cssWidth = canvas.clientWidth || window.innerWidth
    let cssHeight = canvas.clientHeight || window.innerHeight
    const basis = createBasis()

    let lost = false
    let lastNow = 0
    let sampleTime = 0
    let sampleCount = 0

    const observer = typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(entries => {
            const rect = entries[0].contentRect

            cssWidth = rect.width
            cssHeight = rect.height
        })
        : null

    observer?.observe(canvas)

    const onLost = (event: Event): void => {
        event.preventDefault()
        lost = true
        onFailure()
    }

    canvas.addEventListener('webglcontextlost', onLost)

    const sizeCanvas = (): void => {
        const dpr = Math.min(window.devicePixelRatio || 1, 2)
        let scale = dpr * quality
        const pixels = cssWidth * cssHeight * scale * scale

        if (pixels > maxPixels) scale *= Math.sqrt(maxPixels / pixels)

        const width = Math.max(2, Math.round(cssWidth * scale))
        const height = Math.max(2, Math.round(cssHeight * scale))

        if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width
            canvas.height = height
            context.viewport(0, 0, width, height)
        }
    }

    // Keep the frame rate smooth by trading resolution, never motion
    const adapt = (now: number): void => {
        if (lastNow) {
            const delta = now - lastNow

            if (delta < 250) {
                sampleTime += delta
                sampleCount++
            }
        }

        lastNow = now

        if (sampleCount < 45) return

        const average = sampleTime / sampleCount

        sampleTime = 0
        sampleCount = 0

        if (average > 24 && quality > minQuality) quality = Math.max(minQuality, quality * 0.84)
        else if (average < 17.6 && quality < maxQuality) quality = Math.min(maxQuality, quality * 1.05)
    }

    const render = (frame: UniverseFrame): void => {
        if (lost) return

        adapt(performance.now())
        sizeCanvas()

        const params = frame.params
        const boost = frame.boost
        const tilt = frame.tilt
        const camera = computeCamera(params, boost, tilt, canvas.width, canvas.height, basis)

        context.uniform2f(uniforms.u_resolution, canvas.width, canvas.height)
        context.uniform1f(uniforms.u_time, frame.time)
        context.uniform3f(uniforms.u_camPos, camera.px, camera.py, camera.pz)
        context.uniform3f(uniforms.u_camRight, camera.rx, camera.ry, camera.rz)
        context.uniform3f(uniforms.u_camUp, camera.ux, camera.uy, camera.uz)
        context.uniform3f(uniforms.u_camForward, camera.fx, camera.fy, camera.fz)
        context.uniform1f(uniforms.u_tanHalfFov, camera.tanHalf)
        context.uniform2f(uniforms.u_shift, params.shiftX * camera.aspect, params.shiftY)
        context.uniform1f(uniforms.u_disk, params.disk * (1 + 0.35 * boost + 0.4 * tilt))
        context.uniform1f(uniforms.u_ring, params.ring * (1 + 0.3 * boost))
        context.uniform1f(uniforms.u_stars, params.stars)
        context.uniform1f(uniforms.u_jet, params.jet)
        context.uniform1f(uniforms.u_exposure, params.exposure * (1 + 0.12 * boost + 0.1 * tilt))
        context.uniform1f(uniforms.u_reveal, frame.reveal)
        context.uniform2f(uniforms.u_pointer, frame.pointerX, frame.pointerY)
        context.uniform1f(uniforms.u_pointerPull, frame.reducedMotion ? 0 : frame.pointerPull)
        context.uniform1f(uniforms.u_stepScale, quality < 0.45 ? 1.35 : 1)
        context.uniform1f(uniforms.u_streak, params.streak)
        context.drawArrays(context.TRIANGLES, 0, 3)
    }

    const dispose = (): void => {
        observer?.disconnect()
        canvas.removeEventListener('webglcontextlost', onLost)
        context.deleteBuffer(buffer)
        context.deleteProgram(program)
    }

    return { render, dispose }
}
